import {Component,OnInit} from '@angular/core';
import {ListView,TypeComparaison,TypeFilter} from "@domain/common/list-view";
import {ActivatedRoute,Router} from "@angular/router";
import {TypeProfil} from "@domain/user/user";
import {PopulationUsageCollab} from "@domain/admin/entreprise/population/population-usage-collab";
import {PopulationUsageRole} from "@domain/admin/entreprise/population/population-usage-role";
import {
    PopulationUsageCollabComponent
} from "@components/admin/entreprise/populations/population-details/population-usage/population-usage-detail/population-usage-collab/population-usage-collab.component";
import {TranslateService} from "@ngx-translate/core";
import {PopulationUsageRoleComponent} from "@components/admin/entreprise/populations/population-details/population-usage/population-usage-detail/population-usage-role/population-usage-role.component";
import {PageHeaderItem} from "@share/component/page-header/page-header";

/**
 * Composant d'affichage de la page Population Usage Detail
 *
 * @author Lionel Gimenez
 * @date 26/05/2025
 */
@Component({
    host: {'data-test-id': 'population-usage-detail'},
    selector: 'population-usage-detail',
    templateUrl: './population-usage-detail.component.html'
})
export class PopulationUsageDetailComponent implements OnInit {

    /** ListView du collaborateur */
    collabView: ListView<PopulationUsageCollab,PopulationUsageCollabComponent>;

    /** ListView du rôle */
    roleView: ListView<PopulationUsageRole,PopulationUsageRoleComponent>;

    /** Id de la population */
    idPopulation: string;

    /** Liste des différents onglets disponibles */
    listeTabItems: Array<PageHeaderItem>;

    /** Onglet courant */
    selectedItem: PageHeaderItem;

    /**
     * Constructeur
     */
    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private translateService: TranslateService,
    ) {
    }

    /**
     * Hook initialisation
     */
    ngOnInit(): void {
        //On récupère les paramètres de la route
        this.route.paramMap.subscribe(params => {
            const fonction = params.get('fonction');
            this.idPopulation = params.get('idPopulation');

            //On charge la ListView
            this.loadListView(this.idPopulation,Number(fonction));
        });

        //Initialisation des onglets
        this.listeTabItems = [{
            code: Onglets.USAGE,
            libelle: this.translateService.instant('admin.entreprise.populations.onglets.usage')
        }];

        //Sélection du premier onglet par défaut
        this.selectedItem = this.listeTabItems[0];
    }

    /**
     * Chargement de la vue
     */
    loadListView(idPopulation: string,fonction: TypeProfil): void {
        //Si la fonction est collaborateur
        if (fonction === TypeProfil.COLLABORATEUR) {
            //On charge la liste collaborateur
            this.loadListCollabView(idPopulation);
        } else {
            //Sinon on charge la liste rôle
            this.loadListRoleView(idPopulation,fonction);
        }
    }

    /**
     * Chargement de la liste collaborateur
     */
    loadListCollabView(idPopulation: string) {
        this.collabView = new ListView<PopulationUsageCollab,PopulationUsageCollabComponent>({
            uri: `/controller/population/getUsagePopulationCollab?idPopulation=${idPopulation}`,
            title: this.translateService.instant('admin.entreprise.populations.usage.libelle'),
            component: PopulationUsageCollabComponent,
            defaultOrder: 'user.nom,user.prenom',
            //Définition des filtres
            listeFilters: [{
                title: this.translateService.instant('admin.entreprise.utilisateurs.internes.nom'),
                clef: 'user.nom',
                isDefault: true,
                typeComparaison: TypeComparaison[TypeComparaison.LIKE]
            },{
                title: this.translateService.instant('admin.entreprise.utilisateurs.internes.prenom'),
                clef: 'user.prenom',
                isDefault: true,
                typeComparaison: TypeComparaison[TypeComparaison.LIKE]
            },{
                title: this.translateService.instant('admin.entreprise.utilisateurs.internes.matricule'),
                clef: 'user.matricule',
                isDefault: true,
                typeComparaison: TypeComparaison[TypeComparaison.LIKE]
            }],
            isFilter: true
        });

        //Persistence des filtres
        this.collabView.isPersistFilters = true;
        this.collabView.className = 'PopulationUsageDetailComponentCollab';
    }

    /**
     * Chargement de la liste rôle
     */
    loadListRoleView(idPopulation: string,fonction: TypeProfil) {
        this.roleView = new ListView<PopulationUsageRole,PopulationUsageRoleComponent>({
            uri: `/controller/population/getUsagePopulationRole?idPopulation=${idPopulation}&fonction=${fonction}`,
            title: this.translateService.instant('admin.entreprise.populations.usage.libelle'),
            component: PopulationUsageRoleComponent,
            defaultOrder: 'user.nom,user.prenom',
            //Définition des filtres
            listeFilters: [{
                clef: 'dateDebut',
                title: this.translateService.instant('filter.dateDebut'),
                type: TypeFilter[TypeFilter.DATE]
            },{
                clef: 'dateFin',
                title: this.translateService.instant('filter.dateFin'),
                type: TypeFilter[TypeFilter.DATE]
            },{
                title: this.translateService.instant('admin.entreprise.utilisateurs.internes.nom'),
                clef: 'user.nom',
                isDefault: true,
                typeComparaison: TypeComparaison[TypeComparaison.LIKE]
            },{
                title: this.translateService.instant('admin.entreprise.utilisateurs.internes.prenom'),
                clef: 'user.prenom',
                isDefault: true,
                typeComparaison: TypeComparaison[TypeComparaison.LIKE]
            },{
                title: this.translateService.instant('admin.entreprise.utilisateurs.internes.matricule'),
                clef: 'user.matricule',
                isDefault: true,
                typeComparaison: TypeComparaison[TypeComparaison.LIKE]
            },{
                title: this.translateService.instant('admin.entreprise.populations.usage.actif'),
                clef: 'isActif',
                type: TypeFilter[TypeFilter.BOOLEAN],
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                isDefault: false,
                onloadValue: 'true'
            }]
        });

        //Persistence des filtres
        this.roleView.isPersistFilters = true;
        this.roleView.className = 'PopulationUsageDetailComponentRole';
    }

    /**
     * Handler pour un retour en arrière
     */
    onGoBack(): void {
        this.router.navigateByUrl(`Admin/Entreprise/Populations/Population/${this.idPopulation}/Usage`);
    }
}

/**
 * Enum des différents onglets
 */
export enum Onglets {
    USAGE = "Usage"
}